/**
 * Open review items of a beta contract across all its documents: comment
 * threads / field requests (`review_comments.list`) and each document's
 * suggestions (`finetune_get`, main + every IR bilaga). Feeds the margin
 * markers, the signing gate notice and the review screen.
 */

import { useCallback, useMemo, useRef } from 'react';
import type { SuggestionInfo } from '@hyranai/regta-document-ir';
import { tenantApi, type ReviewThread } from '../../api.js';
import { useInvalidate, useTenantQuery } from '../../query.js';
import { MAIN_DOCUMENT, documentKey } from './review-model.js';

export type ReviewItems = {
  threads: ReviewThread[];
  /** Every suggestion per document key (`main` / attachment id), decided ones included. */
  suggestions: ReadonlyMap<string, SuggestionInfo[]>;
  /** Node address of every change seq per document key (adaptation groups), for placing action comments. */
  changeAddresses: ReadonlyMap<string, ReadonlyMap<number, string>>;
  openSuggestions: number;
  openFieldRequests: number;
  openThreads: number;
  writable: boolean;
  loaded: boolean;
  refresh: () => Promise<void>;
};

export const REVIEW_SUGGESTIONS_KEY = 'review_items.suggestions';

export function useReviewItems(
  contractId: string | null,
  bilagaIds: readonly string[],
  refreshKey: string,
): ReviewItems {
  const invalidate = useInvalidate();
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;
  const threadsQ = useTenantQuery(
    'review_comments.list',
    (cid) => tenantApi.review_comments.list({ contract_id: cid as string }),
    contractId ? [contractId] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false, enabled: !!contractId },
  );
  const docs = bilagaIds.join(',');
  const suggestionsQ = useTenantQuery(
    REVIEW_SUGGESTIONS_KEY,
    async (cid, ids) => {
      const keys = [null, ...((ids as string) ? (ids as string).split(',') : [])];
      const views = await Promise.all(
        keys.map((attachmentId) =>
          tenantApi.contracts
            .finetune_get({ contractId: cid as string, ...(attachmentId ? { attachmentId } : {}) })
            .then((v) => {
              const addresses = new Map<number, string>();
              for (const g of v.groups ?? []) for (const seq of g.seqs) addresses.set(seq, g.address);
              return [documentKey(attachmentId), v.suggestions ?? [], addresses] as const;
            })
            .catch(() => [documentKey(attachmentId), [] as SuggestionInfo[], new Map<number, string>()] as const),
        ),
      );
      return views;
    },
    contractId ? [contractId, docs, refreshKey] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false, enabled: !!contractId },
  );

  const refresh = useCallback(async () => {
    if (!contractId) return;
    await Promise.all([
      invalidateRef.current('review_comments.list', contractId),
      invalidateRef.current(REVIEW_SUGGESTIONS_KEY, contractId),
    ]);
  }, [contractId]);

  return useMemo(() => {
    const suggestions = new Map<string, SuggestionInfo[]>((suggestionsQ.data ?? []).map(([k, v]) => [k, [...v]]));
    const changeAddresses = new Map<string, ReadonlyMap<number, string>>((suggestionsQ.data ?? []).map(([k, , a]) => [k, a]));
    if (!suggestions.has(MAIN_DOCUMENT)) suggestions.set(MAIN_DOCUMENT, []);
    let openSuggestions = 0;
    for (const list of suggestions.values()) openSuggestions += list.filter((s) => s.open).length;
    return {
      threads: threadsQ.data?.threads ?? [],
      suggestions,
      changeAddresses,
      openSuggestions,
      openFieldRequests: threadsQ.data?.open_field_requests ?? 0,
      openThreads: threadsQ.data?.open_threads ?? 0,
      writable: threadsQ.data?.writable ?? false,
      loaded: !!threadsQ.data && !!suggestionsQ.data,
      refresh,
    };
  }, [threadsQ.data, suggestionsQ.data, refresh]);
}
